import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Target, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, XCircle, ChevronDown, ChevronRight, LayoutGrid } from 'lucide-react';

// Company Scorecard Thresholds (from Rhino Shield L10 templates)
const L10_THRESHOLDS = {
  marketing: {
    cpl: { target: 404.57, max: 484.94, label: 'CPL', format: 'currency' },
    cps: { target: 909.81, max: 1189.64, label: 'CPS', format: 'currency' },
    roas: { target: 6.66, min: 5, label: 'ROAS', format: 'multiple' },
    com: { target: 15, max: 20, label: 'COM %', format: 'percent' },
  },
  sales: {
    cpsale: { target: 3486.24, max: 4621.82, label: 'CPSale', format: 'currency' },
    close_rate: { target: 40, min: 30, label: 'Close Rate', format: 'percent' },
    nsli: { target: 8000, min: 6000, label: 'NSLI', format: 'currency' },
    demo_rate: { target: 80, min: 60, label: 'Demo Rate', format: 'percent' },
    set_rate: { target: 45, min: 35, label: 'Set Rate', format: 'percent' },
  },
  operations: {
    gm_percent: { target: 55, min: 45, label: 'GM %', format: 'percent' },
    labor_percent: { target: 25, max: 30, label: 'Labor %', format: 'percent' },
    material_percent: { target: 20, max: 25, label: 'Material %', format: 'percent' },
  },
  finance: {
    gross_profit_percent: { target: 55, min: 45, label: 'Gross Profit %', format: 'percent' },
    commission_percent: { target: 15, max: 20, label: 'Commission %', format: 'percent' },
    net_percent: { target: 20, min: 10, label: 'NET %', format: 'percent' },
  }
};

// Format helpers
const formatValue = (value, format) => {
  if (value === null || value === undefined) return '-';
  switch (format) {
    case 'currency':
      if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
      if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
      return `$${Number(value).toFixed(0)}`;
    case 'percent':
      return `${Number(value).toFixed(1)}%`;
    case 'multiple':
      return `${Number(value).toFixed(2)}x`;
    default:
      return Number(value).toFixed(0);
  }
};

// Determine status based on thresholds
const getStatus = (actual, metric) => {
  if (actual === null || actual === undefined) return 'neutral';
  
  const { target, min, max } = metric;
  
  if (max && !min) {
    if (actual <= target) return 'on_pace';
    if (actual <= max) return 'at_risk';
    return 'below_min';
  }
  
  if (min && !max) {
    if (actual >= target) return 'on_pace';
    if (actual >= min) return 'at_risk';
    return 'below_min';
  }
  
  if (min && max) {
    if (actual >= target && actual <= max) return 'on_pace';
    if (actual >= min) return 'at_risk';
    return 'below_min';
  }
  
  if (actual >= target * 0.9) return 'on_pace';
  if (actual >= target * 0.7) return 'at_risk';
  return 'below_min';
};

const statusColors = {
  on_pace: {
    bg: 'bg-green-100 dark:bg-green-900/20',
    text: 'text-green-700 dark:text-green-400',
    border: 'border-green-500',
    icon: CheckCircle2,
  },
  at_risk: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/20',
    text: 'text-yellow-700 dark:text-yellow-400',
    border: 'border-yellow-500',
    icon: AlertTriangle,
  },
  below_min: {
    bg: 'bg-red-100 dark:bg-red-900/20',
    text: 'text-red-700 dark:text-red-400',
    border: 'border-red-500',
    icon: XCircle,
  },
  neutral: {
    bg: 'bg-slate-100 dark:bg-slate-800',
    text: 'text-slate-600 dark:text-slate-400',
    border: 'border-slate-300',
    icon: Target,
  }
};

// Single scorecard row
const ScorecardRow = ({ label, actual, weeklyMin, weeklyTarget, mtdActual, mtdTarget, format, metric }) => {
  const status = getStatus(actual, metric);
  const colors = statusColors[status];
  const StatusIcon = colors.icon;
  
  return (
    <tr className="border-b dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50">
      <td className="px-3 py-2 text-sm font-medium dark:text-white whitespace-nowrap">
        {label}
      </td>
      <td className={`px-3 py-2 text-center text-sm font-semibold ${colors.bg} ${colors.text}`}>
        {formatValue(actual, format)}
      </td>
      <td className="px-3 py-2 text-center text-sm text-muted-foreground">
        {formatValue(weeklyMin, format)}
      </td>
      <td className="px-3 py-2 text-center text-sm text-muted-foreground">
        {formatValue(weeklyTarget, format)}
      </td>
      <td className={`px-3 py-2 text-center text-sm font-semibold ${colors.bg} ${colors.text}`}>
        {formatValue(mtdActual, format)}
      </td>
      <td className="px-3 py-2 text-center text-sm text-muted-foreground">
        {formatValue(mtdTarget, format)}
      </td>
      <td className="px-3 py-2 text-center">
        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${colors.bg}`}>
          <StatusIcon className={`h-3 w-3 ${colors.text}`} />
          <span className={`text-xs font-medium ${colors.text}`}>
            {status === 'on_pace' ? 'On Track' : status === 'at_risk' ? 'At Risk' : status === 'below_min' ? 'Below Min' : '-'}
          </span>
        </div>
      </td>
    </tr>
  );
};

// Collapsible Department Section Header
const CollapsibleDepartmentHeader = ({ title, color, isExpanded, onToggle, metricCount }) => (
  <tr 
    className={`${color} border-b-2 cursor-pointer hover:opacity-90 transition-opacity`}
    onClick={onToggle}
    data-testid={`dept-header-${title.toLowerCase()}`}
  >
    <td colSpan={7} className="px-3 py-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-white">
          {title}
        </span>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs bg-white/20 text-white border-0">
            {metricCount} metrics
          </Badge>
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-white" />
          ) : (
            <ChevronRight className="h-4 w-4 text-white" />
          )}
        </div>
      </div>
    </td>
  </tr>
);

// Storage key for collapse state
const COLLAPSE_STORAGE_KEY = 'rhino1_dept_scorecard_collapse';

// Main Department Scorecard component (renamed from L10)
export const DepartmentScorecardSection = ({ data, loading, weeksInMonth = 4, currentWeek = 2 }) => {
  // Collapse state with localStorage persistence
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem(COLLAPSE_STORAGE_KEY);
      return saved ? JSON.parse(saved) : { marketing: false, sales: false, finance: false, operations: false };
    } catch {
      return { marketing: false, sales: false, finance: false, operations: false };
    }
  });

  // Persist collapse state
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(collapsed));
    } catch {}
  }, [collapsed]);

  const toggleSection = (section) => {
    setCollapsed(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) {
    return (
      <Card className="dark:bg-slate-800/60" data-testid="dept-scorecard-loading">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <LayoutGrid className="h-5 w-5 text-primary" />
            Department Scorecard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="h-10 bg-slate-200 dark:bg-slate-700 rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const metrics = data?.metrics || {};
  const period = data?.period || {};
  
  const calculateWeeklyTarget = (monthlyTarget) => {
    if (!monthlyTarget) return null;
    return monthlyTarget / weeksInMonth;
  };
  
  const calculateWeeklyMin = (monthlyMin) => {
    if (!monthlyMin) return null;
    return monthlyMin / weeksInMonth;
  };

  // Build scorecard data
  const buildScorecardData = () => {
    return {
      marketing: [
        {
          key: 'cpl',
          label: 'Cost Per Lead (CPL)',
          actual: metrics.cpl?.actual,
          weeklyTarget: L10_THRESHOLDS.marketing.cpl.target,
          weeklyMin: L10_THRESHOLDS.marketing.cpl.max,
          mtdActual: metrics.cpl?.actual,
          mtdTarget: L10_THRESHOLDS.marketing.cpl.target,
          format: 'currency',
          metric: L10_THRESHOLDS.marketing.cpl,
        },
        {
          key: 'roas',
          label: 'Return on Ad Spend (ROAS)',
          actual: metrics.roas?.actual,
          weeklyTarget: L10_THRESHOLDS.marketing.roas.target,
          weeklyMin: L10_THRESHOLDS.marketing.roas.min,
          mtdActual: metrics.roas?.actual,
          mtdTarget: L10_THRESHOLDS.marketing.roas.target,
          format: 'multiple',
          metric: L10_THRESHOLDS.marketing.roas,
        },
        {
          key: 'com',
          label: 'Cost of Marketing (COM %)',
          actual: metrics.spend?.actual && metrics.revenue?.actual 
            ? (metrics.spend.actual / metrics.revenue.actual) * 100 
            : null,
          weeklyTarget: L10_THRESHOLDS.marketing.com.target,
          weeklyMin: L10_THRESHOLDS.marketing.com.max,
          mtdActual: metrics.spend?.actual && metrics.revenue?.actual 
            ? (metrics.spend.actual / metrics.revenue.actual) * 100 
            : null,
          mtdTarget: L10_THRESHOLDS.marketing.com.target,
          format: 'percent',
          metric: L10_THRESHOLDS.marketing.com,
        },
      ],
      sales: [
        {
          key: 'appointments',
          label: 'Appointments Set',
          actual: metrics.appointments?.actual || metrics.appointments_set?.actual,
          weeklyTarget: calculateWeeklyTarget(metrics.appointments?.target || metrics.appointments_set?.target),
          weeklyMin: calculateWeeklyMin(metrics.appointments?.minimum || metrics.appointments_set?.minimum),
          mtdActual: metrics.appointments?.actual || metrics.appointments_set?.actual,
          mtdTarget: metrics.appointments?.target || metrics.appointments_set?.target,
          format: 'number',
          metric: { target: metrics.appointments?.target || metrics.appointments_set?.target, min: metrics.appointments?.minimum || metrics.appointments_set?.minimum },
        },
        {
          key: 'set_rate',
          label: 'Set Rate',
          actual: metrics.set_rate?.actual,
          weeklyTarget: L10_THRESHOLDS.sales.set_rate.target,
          weeklyMin: L10_THRESHOLDS.sales.set_rate.min,
          mtdActual: metrics.set_rate?.actual,
          mtdTarget: L10_THRESHOLDS.sales.set_rate.target,
          format: 'percent',
          metric: L10_THRESHOLDS.sales.set_rate,
        },
        {
          key: 'demo_rate',
          label: 'Demo Rate',
          actual: metrics.demo_rate?.actual,
          weeklyTarget: L10_THRESHOLDS.sales.demo_rate.target,
          weeklyMin: L10_THRESHOLDS.sales.demo_rate.min,
          mtdActual: metrics.demo_rate?.actual,
          mtdTarget: L10_THRESHOLDS.sales.demo_rate.target,
          format: 'percent',
          metric: L10_THRESHOLDS.sales.demo_rate,
        },
        {
          key: 'close_rate',
          label: 'Close Rate',
          actual: metrics.close_rate?.actual,
          weeklyTarget: L10_THRESHOLDS.sales.close_rate.target,
          weeklyMin: L10_THRESHOLDS.sales.close_rate.min,
          mtdActual: metrics.close_rate?.actual,
          mtdTarget: L10_THRESHOLDS.sales.close_rate.target,
          format: 'percent',
          metric: L10_THRESHOLDS.sales.close_rate,
        },
        {
          key: 'sales',
          label: 'Total Sales (Count)',
          actual: metrics.sales?.actual,
          weeklyTarget: calculateWeeklyTarget(metrics.sales?.target),
          weeklyMin: calculateWeeklyMin(metrics.sales?.minimum),
          mtdActual: metrics.sales?.actual,
          mtdTarget: metrics.sales?.target,
          format: 'number',
          metric: { target: metrics.sales?.target, min: metrics.sales?.minimum },
        },
        {
          key: 'nsli',
          label: 'Avg Sale (NSLI)',
          actual: metrics.sales?.actual && metrics.revenue?.actual 
            ? metrics.revenue.actual / metrics.sales.actual 
            : null,
          weeklyTarget: L10_THRESHOLDS.sales.nsli.target,
          weeklyMin: L10_THRESHOLDS.sales.nsli.min,
          mtdActual: metrics.sales?.actual && metrics.revenue?.actual 
            ? metrics.revenue.actual / metrics.sales.actual 
            : null,
          mtdTarget: L10_THRESHOLDS.sales.nsli.target,
          format: 'currency',
          metric: L10_THRESHOLDS.sales.nsli,
        },
      ],
      finance: [
        {
          key: 'revenue',
          label: 'Revenue',
          actual: metrics.revenue?.actual,
          weeklyTarget: calculateWeeklyTarget(metrics.revenue?.target),
          weeklyMin: calculateWeeklyMin(metrics.revenue?.minimum),
          mtdActual: metrics.revenue?.actual,
          mtdTarget: metrics.revenue?.target,
          format: 'currency',
          metric: { target: metrics.revenue?.target, min: metrics.revenue?.minimum },
        },
        {
          key: 'spend',
          label: 'Ad Spend',
          actual: metrics.spend?.actual,
          weeklyTarget: calculateWeeklyTarget(metrics.spend?.target),
          weeklyMin: null,
          mtdActual: metrics.spend?.actual,
          mtdTarget: metrics.spend?.target,
          format: 'currency',
          metric: { target: metrics.spend?.target, max: metrics.spend?.target * 1.1 },
        },
      ],
      operations: [], // Placeholder
    };
  };

  const scorecardData = buildScorecardData();

  const departmentConfig = [
    { key: 'marketing', title: 'Marketing', color: 'bg-blue-600' },
    { key: 'sales', title: 'Sales', color: 'bg-green-600' },
    { key: 'finance', title: 'Finance', color: 'bg-purple-600' },
    { key: 'operations', title: 'Operations', color: 'bg-orange-600' },
  ];

  return (
    <Card className="dark:bg-slate-800/60" data-testid="dept-scorecard-section">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <LayoutGrid className="h-5 w-5 text-primary" />
            Department Scorecard
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              Week {currentWeek} of {weeksInMonth}
            </Badge>
            <span className="text-xs text-muted-foreground">
              Click headers to collapse
            </span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Weekly metrics aligned to {period.label || 'current period'} goals
        </p>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="border-b dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide w-48">
                  Metric
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  WOW Actual
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Weekly Min
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Weekly Target
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  MTD Actual
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  MTD Target
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-muted-foreground uppercase tracking-wide w-28">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {departmentConfig.map(({ key, title, color }) => (
                <React.Fragment key={key}>
                  <CollapsibleDepartmentHeader 
                    title={title} 
                    color={color}
                    isExpanded={!collapsed[key]}
                    onToggle={() => toggleSection(key)}
                    metricCount={scorecardData[key]?.length || 0}
                  />
                  
                  {/* Collapsible rows with animation */}
                  {!collapsed[key] && (
                    key === 'operations' && scorecardData[key].length === 0 ? (
                      <tr className="border-b dark:border-slate-700">
                        <td colSpan={7} className="px-3 py-4 text-center text-sm text-muted-foreground italic">
                          Operations metrics (GM %, Labor %, Material %) - data source pending
                        </td>
                      </tr>
                    ) : (
                      scorecardData[key]?.map(({ key: rowKey, ...rowProps }) => (
                        <ScorecardRow key={rowKey} {...rowProps} />
                      ))
                    )
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// Export both names for compatibility
export const L10ScorecardSection = DepartmentScorecardSection;
export default DepartmentScorecardSection;
